/**
 * Field configurations for Project Metadata form
 * This data-driven approach makes it easy to maintain and modify fields
 */

// Picklist labels that map to Salesforce Project__c describe fields.
// Used by usePicklist hook to fetch live options from the describe API.
export const PICKLIST_LABELS = {
  APPEN_PARTNER: "Appen_Partner__c",
  PROGRAM_NAME: "Program_Name__c",
  PROJECT_TYPE: "Project_Type__c",
  DELIVERY_TOOL_ORG: "Delivery_Tool_Org__c",
  JOB_CATEGORY: "Job_Category__c",
  DELIVERY_TOOL_NAME: "Delivery_Tool_Name__c",
  PROJECT_STATUS: "Project_Status__c",
  PROJECT_PAYMENT_METHOD: "Project_Payment_Method__c",
  MERCURY_PROJECT_TYPE: "Mercury_Project_Type__c",
  RELEASE_SYSTEM_TRACKED_DATA: "Release_System_Tracked_Data__c",
  REQUISITION_ACTION: "Requisition_Action__c",
};

// Information Section Fields
export const informationSectionFields = {
  section: "Information",
  key: "information",
  fields: [
    {
      label: "Auditor Project",
      path: "auditorProject",
      type: "checkbox",
      info: "Check if this is an auditor project",
    },
    {
      label: "Project Name",
      path: "name",
      type: "text",
      required: true,
      info: "The name of the project as it appears in Mercury Admin",
    },
    {
      cols: 2,
      fields: [
        {
          label: "Short Project Name",
          path: "shortName",
          type: "text",
          required: true,
          info: "Abbreviated name for display purposes",
        },
        {
          label: "Contributor Project Name",
          path: "contributorProjectName",
          type: "text",
          required: true,
          info: "The name of the project as contributors will see it",
        },
      ],
    },
    {
      label: "Short Description",
      path: "shortDescription",
      type: "textarea",
      required: true,
      info: "Required for Lever setup. A concise summary of the project used in external-facing workflows.",
    },
    {
      label: "Long Description",
      path: "longDescription",
      type: "textarea",
      required: false,
      info: "Used internally to describe project requirements and contributor expectations. Supports LLM summaries.",
    },
    {
      cols: 2,
      fields: [
        {
          label: "Project ID for Reports",
          path: "projectIdForReports",
          type: "text",
          readOnly: true,
          info: "This field is calculated upon save",
        },
        {
          label: "Workday Project ID",
          path: "workdayProjectId",
          type: "text",
        },
      ],
    },
    {
      cols: 2,
      fields: [
        {
          label: "Appen Partner",
          path: "appenPartner",
          type: "select",
          required: true,
          picklistLabel: PICKLIST_LABELS.APPEN_PARTNER,
        },
        {
          label: "Category",
          path: "category",
          type: "select",
          required: false,
          picklistLabel: PICKLIST_LABELS.JOB_CATEGORY,
          info: "Optional - leave blank if not applicable",
        },
      ],
    },
    {
      cols: 2,
      fields: [
        {
          label: "Account",
          path: "account",
          type: "searchable",
          searchType: "account",
          required: true,
          info: "Account ID or name",
        },
        {
          label: "Program Name",
          path: "programName",
          type: "select",
          picklistLabel: PICKLIST_LABELS.PROGRAM_NAME,
        },
      ],
    },
    {
      cols: 2,
      fields: [
        {
          label: "Delivery Tool Org",
          path: "deliveryToolOrg",
          type: "select",
          picklistLabel: PICKLIST_LABELS.DELIVERY_TOOL_ORG,
        },
        {
          label: "Delivery Tool Name",
          path: "deliveryToolName",
          type: "select",
          picklistLabel: PICKLIST_LABELS.DELIVERY_TOOL_NAME,
        },
      ],
    },
    {
      cols: 2,
      fields: [
        {
          label: "Hire Start Date",
          path: "hireStartDate",
          type: "date",
          required: true,
        },
        {
          label: "Predicted Close Date",
          path: "predictedCloseDate",
          type: "date",
          required: true,
        },
      ],
    },
    {
      cols: 2,
      fields: [
        {
          label: "Project Status",
          path: "projectStatus",
          type: "select",
          required: true,
          picklistLabel: PICKLIST_LABELS.PROJECT_STATUS,
        },
        {
          label: "Project Type",
          path: "projectType",
          type: "select",
          required: true,
          picklistLabel: PICKLIST_LABELS.PROJECT_TYPE,
        },
      ],
    },
    {
      label: "Project Priority",
      path: "projectPriority",
      type: "number",
      required: true,
      placeholder: "1-100",
      info: "Priority level (1-100)",
    },
  ],
};

// // People Section - Uses special UserLookupField component
// export const peopleSectionFields = {
//   section: "People",
//   key: "people",
//   specialComponent: "UserLookupField", // Indicates this needs custom rendering
//   fields: [
//     {
//       field: "programManager",
//       label: "Program Manager",
//       required: false,
//       type: "user",
//     },
//     {
//       field: "projectManager",
//       label: "Project Manager",
//       type: "user",
//       searchable: true,
//       required: false,
//     },
//     {
//       field: "qualityLead",
//       label: "Quality Lead",
//       required: false,
//       type: "user",
//     },
//     {
//       field: "productivityLead",
//       label: "Productivity Lead",
//       required: false,
//       type: "user",
//     },
//     {
//       field: "reportingLead",
//       label: "Reporting Lead",
//       required: false,
//       type: "user",
//     },
//     {
//       field: "invoicingLead",
//       label: "Invoicing Lead",
//       required: false,
//       type: "user",
//     },
//     {
//       field: "projectSupportLead",
//       label: "Project Support Lead",
//       required: false,
//       type: "user",
//     },
//     {
//       field: "casesDcSupportTeam",
//       label: "Cases DC Support Team",
//       required: false,
//       type: "checkbox",
//     },
//     {
//       field: "recruitmentLead",
//       label: "Recruitment Lead",
//       required: false,
//       type: "user",
//     },
//     {
//       field: "qualificationLead",
//       label: "Qualification Lead",
//       required: false,
//       type: "user",
//     },
//     {
//       field: "onboardingLead",
//       label: "Onboarding Lead",
//       required: false,
//       type: "user",
//     },
//   ],
// };

// Requirements Section
export const requirementsSectionFields = {
  section: "Requirements for Contributor Active Status",
  key: "requirements",
  fields: [
    {
      label: "Payment Setup Required",
      path: "paymentSetupRequired",
      type: "checkbox",
    },
    {
      label: "Manual Activation Required",
      path: "manualActivationRequired",
      type: "checkbox",
    },
    {
      label: "Client Tool Account Required",
      path: "clientToolAccountRequired",
      type: "checkbox",
    },
  ],
};

// Rates Section
export const ratesSectionFields = {
  section: "Rates",
  key: "rates",
  fields: [
    {
      label: "Project Incentive",
      path: "projectIncentive",
      type: "text",
      readOnly: true,
      info: "This field is calculated upon save",
    },
  ],
};

// Funnel Totals Section
export const funnelTotalsSectionFields = {
  section: "Funnel Totals",
  key: "funnelTotals",
  fields: [
    {
      label: "Total Applied",
      path: "totalApplied",
      type: "text",
      readOnly: true,
      info: "This field is calculated upon save",
    },
    {
      label: "Total Qualified",
      path: "totalQualified",
      type: "text",
      readOnly: true,
      info: "This field is calculated upon save",
    },
  ],
};

// Funnel Stages Section
export const funnelStagesSectionFields = {
  section: "Funnel Stages",
  key: "funnelStages",
  info: "All fields in this section are calculated upon save",
  fields: [
    {
      label: "Invited/Available",
      path: "invitedAvailable",
      type: "text",
      readOnly: true,
    },
    { label: "Registered", path: "registered", type: "text", readOnly: true },
    {
      label: "App Received",
      path: "appReceived",
      type: "text",
      readOnly: true,
    },
    { label: "Qualified", path: "qualified", type: "text", readOnly: true },
    { label: "Matched", path: "matched", type: "text", readOnly: true },
    { label: "Active", path: "active", type: "text", readOnly: true },
    { label: "AC Account", path: "acAccount", type: "text", readOnly: true },
    { label: "Production", path: "production", type: "text", readOnly: true },
    { label: "Applied", path: "applied", type: "text", readOnly: true },
    { label: "Removed", path: "removed", type: "text", readOnly: true },
  ],
};

// Payment Configurations Section
export const paymentConfigurationsSectionFields = {
  section: "Payment Configurations",
  key: "paymentConfigurations",
  fields: [
    {
      label: "Project Payment Method",
      path: "projectPaymentMethod",
      type: "select",
      required: true,
      picklistLabel: PICKLIST_LABELS.PROJECT_PAYMENT_METHOD,
    },
    {
      label: "Require PM Approval for Productivity",
      path: "requirePMApproval",
      type: "checkbox",
    },
    {
      label: "Release System Tracked Data",
      path: "releaseSystemTrackedData",
      type: "select",
      picklistLabel: PICKLIST_LABELS.RELEASE_SYSTEM_TRACKED_DATA,
    },
  ],
};

// Activation Section
export const activationSectionFields = {
  section: "Activation",
  key: "activation",
  fields: [
    {
      label: "Activate Comms Invited",
      path: "activateCommsInvited",
      type: "checkbox",
    },
    {
      label: "Activate Comms Applied",
      path: "activateCommsApplied",
      type: "checkbox",
    },
    {
      label: "Activate Comms Onboarding",
      path: "activateCommsOnboarding",
      type: "checkbox",
    },
    {
      label: "Activate Comms Failed",
      path: "activateCommsFailed",
      type: "checkbox",
    },
  ],
};

// Lever Requisition Actions Section
export const leverRequisitionActionsSectionFields = {
  section: "Lever Requisition Actions",
  key: "leverRequisitionActions",
  fields: [
    {
      label: "Requisition Action",
      path: "requisitionAction",
      type: "select",
      picklistLabel: PICKLIST_LABELS.REQUISITION_ACTION,
    },
    {
      label: "Requisition Status",
      path: "requisitionStatus",
      type: "text",
      readOnly: true,
      info: "This field is calculated upon save",
    },
  ],
};

// All sections combined
export const allSections = [
  informationSectionFields,
  // peopleSectionFields,
  requirementsSectionFields,
  ratesSectionFields,
  funnelTotalsSectionFields,
  funnelStagesSectionFields,
  paymentConfigurationsSectionFields,
  activationSectionFields,
  leverRequisitionActionsSectionFields,
];

// Required fields for validation
export const requiredFields = {
  name: "Project Name",
  shortName: "Short Project Name",
  contributorProjectName: "Contributor Project Name",
  shortDescription: "Short Description",
  appenPartner: "Appen Partner",
  account: "Account",
  hireStartDate: "Hire Start Date",
  predictedCloseDate: "Predicted Close Date",
  projectStatus: "Project Status",
  projectType: "Project Type",
  projectPriority: "Project Priority",
  projectPaymentMethod: "Project Payment Method",
};
